import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Ticket, Loader2, Zap, Flame, Trophy } from "lucide-react";
import { Snow } from "../components/Snow";
import { Countdown } from "../components/Countdown";
import { FadeUp, MaskedLine, SectionHeading } from "../components/Reveal";
import { NEW_IMAGES } from "../site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=160+Raceway+Dr+Maynardville+TN+37807";

const DETAILS = [
  { icon: Calendar, label: "Date", value: "December 5, 2026", id: "date" },
  { icon: Clock, label: "Time", value: "10:00 AM — 6:00 PM", id: "time" },
  { icon: MapPin, label: "Location", value: "160 Raceway Dr, Maynardville, TN", id: "location", href: MAPS_URL },
  { icon: Ticket, label: "Truck Entry", value: "$40 per truck", id: "price" },
];

const PAYOUTS = [
  { place: "1st Place", amount: "$500", extra: "Plus Best Burnout Trophy", id: "first" },
  { place: "2nd Place", amount: "$300", extra: "Cash payout", id: "second" },
  { place: "3rd Place", amount: "$100", extra: "Cash payout", id: "third" },
];

export default function Show() {
  const [loading, setLoading] = useState(null);

  const register = async (entryType) => {
    setLoading(entryType);
    try {
      const { data } = await axios.post(`${API}/show/checkout`, {
        origin_url: window.location.origin,
        entry_type: entryType,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please call us instead.");
      setLoading(null);
    }
  };

  return (
    <main data-testid="show-page">
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24">
        <img
          src={NEW_IMAGES.nightWheels}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
        <Snow className="pointer-events-none absolute inset-0 h-full w-full" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <FadeUp>
              <p className="mb-5 inline-flex items-center gap-2 clip-badge border border-[#DC2626]/50 bg-[#DC2626]/10 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#DC2626]">
                <Zap size={13} fill="currentColor" /> Second Annual
              </p>
            </FadeUp>
            <h1 className="font-display font-black uppercase italic tracking-tighter leading-[0.85]" data-testid="show-headline">
              <MaskedLine delay={0.15} className="text-6xl sm:text-8xl text-white">
                Toy Bash
              </MaskedLine>
              <MaskedLine delay={0.32} className="text-4xl sm:text-6xl text-[#DC2626] glow-red">
                Truck Show & Burnout Competition
              </MaskedLine>
            </h1>
            <FadeUp delay={0.55}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-300">
                Bring your build out December 5th. Show entry is $40 per truck — and the burnout competition pays out $500, $300 and $100.
              </p>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Countdown to gates open
              </p>
              <Countdown />
            </FadeUp>

            <div className="mt-10 grid grid-cols-2 gap-4 max-w-xl">
              {DETAILS.map((d, i) => {
                const inner = (
                  <>
                    <d.icon size={18} className="text-[#22C55E] mb-3" />
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">{d.label}</p>
                    <p className="mt-1 font-display text-lg sm:text-xl font-bold uppercase tracking-tight text-white leading-tight">{d.value}</p>
                  </>
                );
                return (
                  <FadeUp key={d.id} delay={0.65 + i * 0.08}>
                    {d.href ? (
                      <a href={d.href} target="_blank" rel="noopener noreferrer" data-testid={`show-detail-${d.id}`} className="clip-corner block h-full border border-zinc-700/80 bg-black/60 backdrop-blur p-5 transition-colors hover:border-[#DC2626]/50">
                        {inner}
                      </a>
                    ) : (
                      <div data-testid={`show-detail-${d.id}`} className="clip-corner h-full border border-zinc-700/80 bg-black/60 backdrop-blur p-5">
                        {inner}
                      </div>
                    )}
                  </FadeUp>
                );
              })}
            </div>

            <FadeUp delay={0.95}>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  onClick={() => register("show")}
                  disabled={loading !== null}
                  data-testid="show-register-button"
                  className="clip-badge inline-flex items-center gap-3 bg-[#DC2626] px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:bg-[#EF4444] disabled:opacity-60"
                >
                  {loading === "show" ? <Loader2 size={20} className="animate-spin" /> : <Ticket size={20} />}
                  Enter the show — $40
                </button>
                <button
                  onClick={() => register("burnout")}
                  disabled={loading !== null}
                  data-testid="burnout-register-button"
                  className="clip-badge inline-flex items-center gap-3 bg-[#16A34A] px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-[#22C55E] hover:text-black disabled:opacity-60"
                >
                  {loading === "burnout" ? <Loader2 size={20} className="animate-spin" /> : <Flame size={20} />}
                  Enter the burnout — $50
                </button>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Secure card checkout by Stripe · Payment = your RSVP
              </p>
            </FadeUp>
          </div>

          <FadeUp delay={0.4}>
            <div className="clip-corner hud-frame relative overflow-hidden border border-zinc-700" data-testid="show-flyer">
              <img
                src={NEW_IMAGES.flyer}
                alt="ETN Customz Toy Bash official event flyer — December 5th, burnout competition, Maynardville TN"
                className="w-full h-auto block"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24 sm:pb-32" data-testid="burnout-section">
        <SectionHeading
          eyebrow="Burnout Competition"
          title={<>Smoke it.<br /><span className="text-outline-red">Get paid.</span></>}
          className="mb-12"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {PAYOUTS.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.08}>
              <div
                data-testid={`payout-${p.id}`}
                className={`clip-corner relative border p-8 text-center transition-colors duration-500 ${
                  i === 0 ? "border-[#DC2626]/60 bg-[#DC2626]/5" : "border-zinc-800 bg-carbon"
                }`}
              >
                {i === 0 && <Trophy size={22} className="mx-auto mb-3 text-[#DC2626]" />}
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">{p.place}</p>
                <p className={`mt-2 font-display text-6xl font-black italic tracking-tighter ${i === 0 ? "text-[#DC2626] glow-red" : "text-white"}`}>
                  {p.amount}
                </p>
                <p className="mt-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#22C55E]">{p.extra}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div className="clip-badge mx-auto mt-8 w-fit border border-zinc-700 bg-carbon px-6 py-3" data-testid="burnout-rule">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-zinc-300 text-center">
              $50 entry to compete · Each driver gets 2 minutes to do a burnout
            </p>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
